import React from 'react';
import ReactApexChart from 'react-apexcharts';
import CircularLoader from '../../components/CircularLoader';
import axios from 'axios';
import Cookies from 'js-cookie';

const pieChartOptions = {
    chart: {
        type: 'donut',
        animations: {
            enabled: true,
            easing: 'easeinout',
            speed: 800,
            animateGradually: {
                enabled: true,
                delay: 150
            },
            dynamicAnimation: {
                enabled: true,
                speed: 350
            }
        }
    },
    dataLabels: {
        enabled: true,
        formatter: function (val) {
            return val.toFixed(1) + '%';
        }
    },
    tooltip: {
        y: {
            formatter: function (val) {
                return val.toFixed(1);
            }
        }
    },
    plotOptions: {
        pie: {
            expandOnClick: true
        }
    },
    legend: {
        position: 'bottom',
        fontSize: '13px',
        itemMargin: {
            horizontal: 10,
            vertical: 5
        }
    },
    colors: ['#94BBD4', '#FFBD88']
};

/**
 * `RevenueBreakdownChart` is a React component that displays a donut chart of revenue breakdown
 * for a selected practice and location within a specific date range. It fetches and visualizes
 * data from an API, showing a loading indicator while fetching and handling empty or erroneous
 * responses gracefully.
 * 
 * The chart animations and aesthetics are configured via the `pieChartOptions` object, with dynamic
 * data labels and tooltips formatted to display percentages.
 * 
 * @param {Object} props - Component props.
 * @param {string} props.practice - The practice identifier for filtering the chart data.
 * @param {string} props.location - The location identifier for filtering the chart data.
 * @param {string} props.startDate - The start date of the date range filter.
 * @param {string} props.endDate - The end date of the date range filter.
 */
class RevenueBreakdownChart extends React.Component {
    /**
     * Constructs the RevenueBreakdownChart component with default state.
     * @param {Object} props - The props passed to the component.
     * 
     * @state {Object} state - The internal state of the component.
     * @state {Array} state.series - The data series for the chart.
     * @state {Object} state.options - The chart configuration options, including styling and animations.
     * @state {boolean} state.isLoading - Indicates whether the chart data is being fetched.
     */
    constructor(props) {
        super(props);

        this.state = {
            series: [],
            options: pieChartOptions,
            isLoading: false
        };
    }

    /**
     * React lifecycle method called after the component updates. It triggers a new data fetch
     * if the practice, location, startDate, or endDate props have changed.
     * @param {Object} prevProps - Previous props for comparison.
     */
    componentDidUpdate(prevProps) {
        if (prevProps.practice !== this.props.practice ||
            prevProps.location !== this.props.location ||
            prevProps.startDate !== this.props.startDate ||
            prevProps.endDate !== this.props.endDate) {
            this.fetchChartData();
        }
    }

    /**
     * Fetches chart data based on the practice, location, startDate, and endDate props. Updates the component's
     * state with the new data for rendering the chart or logs an error if the fetch fails.
     */
    fetchChartData() {
        this.setState({ isLoading: true });
        const { practice, location, startDate, endDate } = this.props;
        const url = `${process.env.REACT_APP_BASE_URL}admin/revenue_breakdown?practice=${practice}&location=${location}&startDate=${startDate}&endDate=${endDate}`;
           const token = axios.defaults.headers.common.Authorization;
                     
                     const config = {
                     headers: {
                       Authorization: `${token}`,
                     },
                   };

        axios.get(url, config)
            .then(response => {
                if (!Array.isArray(response.data)) {
                    this.setState({ series: [], isLoading: false });
                    return;
                }
                const newSeries = response.data.map(item => item.value);
                const newLabels = response.data.map(item => item.label);
                this.setState(prevState => ({
                    series: newSeries,
                    options: {
                        ...prevState.options,
                        labels: newLabels
                    },
                    isLoading: false
                }));
            })
            .catch(error => {
                console.error('Error fetching chart data:', error);
                this.setState({ isLoading: false });
            });
    }

    /**
     * React lifecycle method called immediately after the component is mounted.
     * It triggers the initial data fetch for the chart.
     */
    componentDidMount() {
        this.fetchChartData();
    }

    /**
     * Renders the donut chart using ReactApexChart with the fetched data or displays
     * a loading state while data is being fetched.
     * 
     * @returns {React.ReactElement} The rendered component, which might include a loader, an empty state message, or the chart itself.
     */
    render() {
        const containerStyle = {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '200px'
        };
        const allValuesAreZero = this.state.series.every(value => value === 0);
        return (
            <div id="homepage-RevenueBreakdownChart" style={this.state.isLoading ? containerStyle : {}}>
                {this.state.isLoading ? (
                    <CircularLoader />
                ) : allValuesAreZero ? (
                    <div style={containerStyle}>No data matches these filters.</div>
                ) : (
                    <ReactApexChart options={this.state.options} series={this.state.series} type="donut" width={400} />
                )}
            </div>
        );
    }
}

export default RevenueBreakdownChart;

